import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { McpSectionNavHeader } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/section-nav-header";
import {
  McpServerCard,
  type McpServerCardSelectionState,
} from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/server-card";
import { McpToolCard } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/tool-card";
import { McpToolkitGroupHeader } from "@arcadeai/design-system/components/ui/molecules/mcp-server-picker/toolkit-group-header";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";

export type PickerTool = {
  /** Version-less tool ref, e.g. `"Gmail.SendEmail"`. */
  ref: string;
  /** Tool name with the toolkit prefix removed, e.g. `"SendEmail"`. */
  name: string;
};

export type PickerGroup = {
  /** Toolkit display name / prefix, e.g. `"Gmail"`. */
  toolkit: string;
  tools: PickerTool[];
};

type ToolPickerGridProps = {
  groups: PickerGroup[];
  selected: ReadonlySet<string>;
  onToggle: (ref: string, checked: boolean) => void;
  onToggleMany: (refs: string[], checked: boolean) => void;
  /** Shown when there are no groups at all (before any search). */
  emptyLabel?: string;
};

const selectionState = (
  selectedCount: number,
  total: number
): McpServerCardSelectionState => {
  if (selectedCount === 0) {
    return "none";
  }
  if (selectedCount === total) {
    return "selected";
  }
  return "partial";
};

// Bright-green highlight for chosen servers, matching the connect panel so the
// whole flow reads as one brand surface. tailwind-merge lets these border/ring
// classes win over the card's neutral selected shell.
const accentClass = (state: McpServerCardSelectionState): string => {
  if (state === "selected") {
    return "border-primary/60 bg-primary/5 ring-1 ring-primary/25";
  }
  if (state === "partial") {
    return "border-primary/50 bg-primary/5";
  }
  return "";
};

export const ToolPickerGrid = ({
  groups,
  selected,
  onToggle,
  onToggleMany,
  emptyLabel = "No tools available.",
}: ToolPickerGridProps) => {
  const [query, setQuery] = useState("");
  const [drill, setDrill] = useState<string | null>(null);

  const q = query.trim().toLowerCase();

  const matchesQuery = useMemo(
    () =>
      (group: PickerGroup): boolean => {
        if (!q) {
          return true;
        }
        if (group.toolkit.toLowerCase().includes(q)) {
          return true;
        }
        return group.tools.some(
          (tool) =>
            tool.name.toLowerCase().includes(q) ||
            tool.ref.toLowerCase().includes(q)
        );
      },
    [q]
  );

  const drillGroup = useMemo(
    () => groups.find((group) => group.toolkit === drill) ?? null,
    [groups, drill]
  );

  if (groups.length === 0) {
    return (
      <p className="rounded-xl border border-dashed p-8 text-center text-muted-foreground text-sm">
        {emptyLabel}
      </p>
    );
  }

  if (drillGroup) {
    const refs = drillGroup.tools.map((tool) => tool.ref);
    const selectedInGroup = refs.filter((ref) => selected.has(ref)).length;
    const tools = q
      ? drillGroup.tools.filter(
          (tool) =>
            tool.name.toLowerCase().includes(q) ||
            tool.ref.toLowerCase().includes(q)
        )
      : drillGroup.tools;
    // Select all / Clear act on the tools currently visible (a search narrows
    // them), so the buttons never touch tools the user can't see.
    const visibleRefs = tools.map((tool) => tool.ref);

    return (
      <div className="space-y-4">
        <McpSectionNavHeader
          actions={
            <>
              <Button
                onClick={() => onToggleMany(visibleRefs, true)}
                size="sm"
                variant="ghost"
              >
                Select all
              </Button>
              <Button
                onClick={() => onToggleMany(visibleRefs, false)}
                size="sm"
                variant="ghost"
              >
                Clear
              </Button>
            </>
          }
          backLabel="All servers"
          identity={
            <McpToolkitGroupHeader
              count={`${selectedInGroup}/${refs.length} selected`}
              name={drillGroup.toolkit}
              toolkitId={drillGroup.toolkit}
            />
          }
          onBack={() => setDrill(null)}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          {tools.map((tool) => {
            const isSelected = selected.has(tool.ref);
            return (
              <McpToolCard
                className={cn(
                  isSelected &&
                    "border-primary/60 bg-primary/5 ring-1 ring-primary/25"
                )}
                key={tool.ref}
                name={tool.name}
                onToggle={() => onToggle(tool.ref, !isSelected)}
                rawName={tool.ref}
                selected={isSelected}
              />
            );
          })}
        </div>
      </div>
    );
  }

  const visibleGroups = groups.filter(matchesQuery);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label="Search tools"
          className="pl-9"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search servers and tools"
          value={query}
        />
      </div>

      {visibleGroups.length === 0 ? (
        <p className="rounded-xl border border-dashed p-8 text-center text-muted-foreground text-sm">
          No servers or tools match “{query}”.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {visibleGroups.map((group) => {
            const refs = group.tools.map((tool) => tool.ref);
            const selectedCount = refs.filter((ref) =>
              selected.has(ref)
            ).length;
            const state = selectionState(selectedCount, refs.length);
            return (
              <McpServerCard
                className={accentClass(state)}
                key={group.toolkit}
                name={group.toolkit}
                onCustomize={() => setDrill(group.toolkit)}
                onToggle={() => onToggleMany(refs, state !== "selected")}
                selectedToolCount={selectedCount}
                selectionState={state}
                toolCount={refs.length}
                toolkitId={group.toolkit}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
